import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  FormProps,
  notification,
  Row,
  Select,
  Table,
  TableColumnsType,
  TableProps,
  Button,
} from 'antd';
import Title from 'antd/es/typography/Title';
import locale from 'antd/es/date-picker/locale/th_TH';
import dayjs from 'dayjs';
import { useState } from 'react';
import { docTypeInvoice, formatDate } from '@/utilities/dataUtils';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { InvoiceList } from '@/interfaces/seller';
import CustomerSelector from '@/components/selector/customer-selector';
import { ICustomer } from '@/interfaces/customer';
import { IInvoiceNo, IServerInfoRes } from '@/interfaces/misc';
import { getServerInfo } from '@/adapters/misc';
import InvoiceNoSelector from '@/components/selector/invoice-no-selector';
import InvoiceModal from '@/components/modal/invoice-modal';
import classNames from 'classnames';
import {
  getInvoiceStatusOptions,
  getInvoiceStatus,
  getInvoiceStatusColor,
  getInvoiceTypeOptions,
} from '@/helpers/invoice';
import { jobProcess } from '@/adapters/job';
import { getQueryParam } from '@/utilities/queryUtils';
import { invoiceEntry, invoiceVerify, searchInvoice } from '@/adapters/invoice';
import { IInvoice } from '@/interfaces/invoice';

const { RangePicker } = DatePicker;

interface ISearchInvoiceReq {
  invoiceNoFrom: string | null;
  invoiceNoTo: string | null;
  dateFrom: string;
  dateTo: string;
  docStatus: string;
  customerFrom: string | null;
  customerTo: string | null;
  docDate?: any;
  invoiceType?: string;
}

const _criteria: ISearchInvoiceReq = {
  invoiceNoFrom: null,
  invoiceNoTo: null,
  dateFrom: dayjs().startOf('month').format('YYYY-MM-DD'),
  dateTo: dayjs().endOf('month').format('YYYY-MM-DD'),
  docStatus: 'PENDING',
  customerFrom: null,
  customerTo: null,
  invoiceType: 'INV',
};

const InvoicePage: React.FC = () => {
  const job = getQueryParam('job');
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState<ISearchInvoiceReq>({
    ..._criteria,
  });
  const [selectedRowKey, setSelectedRowKey] = useState<IInvoice[]>([]);
  const [dateRange, setDateRange] = useState({
    dateFrom: _criteria.dateFrom,
    dateTo: _criteria.dateTo,
  });
  const [invoiceType, setInvoiceType] = useState<string | undefined>(
    _criteria.invoiceType
  );

  const customerFromValue = Form.useWatch('customerFrom', form);
  const invoiceNoFromValue = Form.useWatch('invoiceNoFrom', form);

  const [invoiceModal, setInvoiceModal] = useState<{
    open: boolean;
    invoiceList: InvoiceList[];
  }>({
    open: false,
    invoiceList: [],
  });

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  };

  const onFinish: FormProps<ISearchInvoiceReq>['onFinish'] = (values) => {
    console.log('values', values);
    const dateFrom = values.docDate?.[0].format('YYYY-MM-DD') || '';
    const dateTo = values.docDate?.[1].format('YYYY-MM-DD') || '';
    setCriteria({
      ...values,
      dateFrom,
      dateTo,
      docDate: values.docDate,
      invoiceType: values.invoiceType || '',
    });
    setDateRange({ dateFrom, dateTo });
    setInvoiceType(values.invoiceType || '');
  };

  const onClear = () => {
    form.setFieldsValue({
      ..._criteria,
      docDate: [dayjs(_criteria.dateFrom), dayjs(_criteria.dateTo)],
    });
    setCriteria({ ..._criteria });
    setDateRange({
      dateFrom: _criteria.dateFrom,
      dateTo: _criteria.dateTo,
    });
    setInvoiceType(_criteria.invoiceType);
  };

  const columns: TableColumnsType<IInvoice> = [
    {
      title: '#',
      dataIndex: '',
      width: 40,
      align: 'center',
      className: 'text-bold',
      render: (_, rec, i) => <div className="font-weight-bold">{i + 1}</div>,
    },
    {
      title: 'เลขที่เอกสาร',
      dataIndex: 'ID',
      align: 'left',
      className: 'text-bold',
      width: 80,
      fixed: 'left',
    },
    {
      title: 'ประเภทเอกสาร',
      dataIndex: 'TYPECODE',
      align: 'center',
      className: 'text-bold',
      width: 80,
      render: (_) => docTypeInvoice(_),
    },
    {
      title: 'วันที่เอกสาร',
      dataIndex: 'ISSUEDATETIME',
      align: 'center',
      width: 80,
      render: (_) => formatDate(_),
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'BUYER_NAME_TEXT',
      align: 'left',
      className: 'text-bold',
      width: 300,
    },
    {
      title: 'สถานะการส่ง',
      dataIndex: 'AR_MSTR_BMESSSTATUS',
      align: 'center',
      width: 10,
      render: (_) => (
        <div style={{ backgroundColor: getInvoiceStatusColor(_) }}>
          {getInvoiceStatus(_)}
        </div>
      ),
    },
  ];

  const { data, isLoading, refetch } = useQuery<AxiosResponse<any>, Error>({
    queryKey: ['search-invoice', criteria],
    queryFn: async () => await searchInvoice(criteria),
  });

  const { data: serverInfo } = useQuery<AxiosResponse<IServerInfoRes>, Error>({
    queryKey: ['search-server-info'],
    queryFn: async () => await getServerInfo(),
  });

  const invoices: IInvoice[] = data?.data?.data || [];

  const rowSelection: TableProps<IInvoice>['rowSelection'] = {
    selectedRowKeys: selectedRowKey.map((row) => row.ID),
    onChange: (selectedRowKeys: React.Key[], selectedRows: IInvoice[]) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        'selectedRows: ',
        selectedRows
      );
      setSelectedRowKey(selectedRows);
    },
    getCheckboxProps: (record: IInvoice) => ({
      disabled: !!record.AR_MSTR_BMESSID && record.AR_MSTR_BMESSSTATUS !== 'PC',
    }),
  };

  const mutations = useMutation({
    mutationFn: async (data: IInvoice[] | IInvoice) => await invoiceEntry(data),
    mutationKey: ['invoice-entry'],
    onSuccess: (data) => {
      const invoices: IInvoice[] = data.data?.data || [];
      invoices.forEach((res: any) => {
        if (res.status === 'fulfilled') {
          const refNo = res.value?.refNo || '';
          notification.success({
            message: `${refNo} ส่งเอกสารสำเร็จ`,
          });
        } else {
          const refNo = res.reason?.refNo || '';
          const errorMessage = res.reason?.message || 'ส่งเอกสารไม่สำเร็จ';
          notification.error({
            message: `${refNo} ${errorMessage}`,
          });
        }
      });
      const hasSuccess = invoices.some(
        (res: any) => res.status === 'fulfilled'
      );
      if (hasSuccess) {
        refetch();
        setSelectedRowKey([]);
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onEntry(): void {
    mutations.mutateAsync(selectedRowKey);
  }

  const mutationsVerify = useMutation({
    mutationFn: async (data: IInvoice[] | IInvoice) =>
      await invoiceVerify(data),
    mutationKey: ['invoice-verify'],
    onSuccess: (data) => {
      const invoices: IInvoice[] = data.data?.data || [];
      invoices.forEach((res: any) => {
        if (res.status === 'fulfilled') {
          const refNo = res.value?.refNo || '';
          notification.success({
            message: `${refNo} ตรวจสอบสำเร็จ`,
          });
        } else {
          const refNo = res.reason?.refNo || '';
          const errorMessage = res.reason?.message || 'ตรวจสอบไม่สำเร็จ';
          notification.error({
            message: `${refNo} ${errorMessage}`,
          });
        }
      });
      const hasSuccess = invoices.some(
        (res: any) => res.status === 'fulfilled'
      );
      if (hasSuccess) {
        refetch();
        setSelectedRowKey([]);
      }
    },
    onError: (error) => {
      console.log('error', error);
    },
  });

  function onVerify(): void {
    mutationsVerify.mutateAsync(selectedRowKey);
  }

  return (
    <>
      <Flex justify="space-between" gap={2}>
        <div>
          <Title level={2} className="text-center">
            {serverInfo?.data?.data?.[0]?.SERVER_NAME}
          </Title>
        </div>
        <div>
          <Title
            level={2}
            className="text-center"
            onClick={() => (job === 'x12' ? jobProcess('invoice') : undefined)}
          >
            ค้นหาใบแจ้งหนี้/ใบกำกับภาษี
          </Title>
        </div>
      </Flex>
      <Card>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...criteria,
            docDate: [dayjs(criteria.dateFrom), dayjs(criteria.dateTo)],
          }}
          onFinish={onFinish}
          autoComplete="off"
          labelAlign={'left'}
        >
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item name="invoiceNoFrom" label="เลขที่เอกสาร (จาก)">
                <InvoiceNoSelector
                  dateFrom={dateRange.dateFrom}
                  dateTo={dateRange.dateTo}
                  invoiceType={invoiceType}
                  handleSelectData={(invoiceNo: IInvoiceNo) => {
                    form.setFieldsValue({
                      invoiceNoFrom: invoiceNo?.ID,
                      ...(!invoiceNo?.ID && { invoiceNoTo: null }),
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="invoiceNoTo" label="เลขที่เอกสาร (ถึง)">
                <InvoiceNoSelector
                  disabled={!invoiceNoFromValue}
                  dateFrom={dateRange.dateFrom}
                  dateTo={dateRange.dateTo}
                  invoiceType={invoiceType}
                  handleSelectData={(invoiceNo: IInvoiceNo) => {
                    form.setFieldsValue({
                      invoiceNoTo: invoiceNo?.ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<ISearchInvoiceReq>
                name="customerFrom"
                label="ลูกค้า (จาก)"
              >
                <CustomerSelector
                  handleSelectData={function (customer: ICustomer): void {
                    form.setFieldsValue({
                      customerFrom: customer?.CMAD_ID,
                      ...(!customer?.CMAD_ID && { customerTo: null }),
                    });
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<ISearchInvoiceReq>
                name="customerTo"
                label="ลูกค้า (ถึง)"
              >
                <CustomerSelector
                  disabled={!customerFromValue}
                  handleSelectData={function (customer: ICustomer): void {
                    form.setFieldsValue({
                      customerTo: customer?.CMAD_ID,
                    });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item<ISearchInvoiceReq>
                name="invoiceType"
                label="ประเภทเอกสาร"
              >
                <Select
                  allowClear
                  options={getInvoiceTypeOptions()}
                  placeholder="เลือกเอกสาร"
                  size="large"
                  onChange={(value) => setInvoiceType(value)}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name={'docDate'} label="วันที่เอกสาร">
                <RangePicker
                  size="large"
                  style={{ width: '100%' }}
                  locale={locale}
                  allowClear={false}
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      setDateRange({
                        dateFrom: dates[0].format('YYYY-MM-DD'),
                        dateTo: dates[1].format('YYYY-MM-DD'),
                      });
                      form.setFieldsValue({
                        invoiceNoFrom: null,
                        invoiceNoTo: null,
                      });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item<ISearchInvoiceReq> name="docStatus" label="สถานะ">
                <Select
                  allowClear
                  options={getInvoiceStatusOptions()}
                  placeholder="เลือกสถานะ"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>
          <Flex gap={24} justify="center">
            <Button
              disabled={
                !selectedRowKey.length ||
                mutations.isPending ||
                selectedRowKey.some((key) => key.AR_MSTR_BMESSSTATUS === 'PC')
              }
              size="large"
              color="cyan"
              variant="solid"
              style={{ minWidth: 120 }}
              onClick={() => onEntry()}
            >
              {mutations.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ส่งเอกสาร'
              )}
            </Button>
            <Button
              disabled={
                !selectedRowKey.length ||
                selectedRowKey.some(
                  (key) => key.AR_MSTR_BMESSSTATUS !== 'PC'
                ) ||
                mutationsVerify.isPending
              }
              size="large"
              color="orange"
              variant="solid"
              style={{ minWidth: 120 }}
              onClick={() => onVerify()}
            >
              {mutationsVerify.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ตรวจสอบ'
              )}
            </Button>

            <Button
              size="large"
              htmlType="submit"
              className={classNames('bg-primary text-white')}
              variant="solid"
              style={{ minWidth: 120 }}
              loading={isLoading}
            >
              ค้นหา
            </Button>
            <Button
              size="large"
              color="danger"
              variant="solid"
              onClick={onClear}
              style={{ minWidth: 120 }}
            >
              ยกเลิก
            </Button>
          </Flex>
        </Form>
      </Card>
      <Card className="mt-3">
        <Table<IInvoice>
          rowSelection={{ type: 'checkbox', ...rowSelection }}
          columns={columns}
          dataSource={invoices}
          size="small"
          bordered
          rowKey="ID"
          pagination={{
            total: invoices?.length || 0,
            showTotal: (total) => `${total} รายการ`,
            showSizeChanger: true,
            locale: { items_per_page: '/หน้า' },
          }}
          locale={{
            emptyText: 'ไม่พบรายการ',
          }}
        />
      </Card>
      <InvoiceModal
        open={invoiceModal?.open || false}
        invoiceList={invoiceModal.invoiceList || []}
        onClose={function (): void {
          setInvoiceModal({
            invoiceList: [],
            open: false,
          });
        }}
      />
    </>
  );
};

export default InvoicePage;
